// ============================================================
// MIN-HEAP - the priority queue under the movement flood fill.
//
// Keys with a cost, popped cheapest first. Split out of movement.ts,
// which is the only user. Pure. Zero React dependency.
// ============================================================

/** A tiny binary heap - Dijkstra over a big map should not be quadratic. */
export class MinHeap {
  private items: { key: string; cost: number }[] = [];

  push(key: string, cost: number): void {
    this.items.push({ key, cost });
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent].cost <= this.items[i].cost) break;
      [this.items[parent], this.items[i]] = [this.items[i], this.items[parent]];
      i = parent;
    }
  }

  pop(): { key: string; cost: number } | undefined {
    const top = this.items[0];
    const last = this.items.pop();
    if (this.items.length && last) {
      this.items[0] = last;
      this.siftDown();
    }
    return top;
  }

  /** Walk the new root down until neither child is cheaper. */
  private siftDown(): void {
    let i = 0;
    for (;;) {
      const smallest = this.cheapest(this.cheapest(i, 2 * i + 1), 2 * i + 2);
      if (smallest === i) return;
      [this.items[smallest], this.items[i]] = [this.items[i], this.items[smallest]];
      i = smallest;
    }
  }

  /** `child` if it exists and is strictly cheaper than `best`, otherwise `best`. */
  private cheapest(best: number, child: number): number {
    return child < this.items.length && this.items[child].cost < this.items[best].cost ? child : best;
  }

  get size(): number {
    return this.items.length;
  }
}
